import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Skeleton, Typography } from 'antd';

import TermText from './TermText';
import UserAvatar from '../users/UserAvatar';
import { previewNotificationDef, sendNotificationDefTest } from '../../services/notifications';
import { getUsersByIds } from '../../services/users';
import { resolveChannels } from '../../config/notificationChannels';
import { apiErrorMessage } from '../../utils/apiErrors';
import { formatCount, joinMeta } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';

const { Text, Paragraph } = Typography;

// Enough faces to recognise the audience, short enough that the rail stays a
// rail. The overflow is COUNTED rather than hidden — a list that quietly
// stopped at eight would be the rail telling the same lie it exists to catch.
const MAX_NAMES = 8;
// The same bargain for the ladder's records.
const MAX_WATCH_ITEMS = 8;

/**
 * What a channel refused, in words. The codes are
 * `keepr-api utils/notifications/channels/shared.js checkSupports` plus the two
 * each channel adds for itself, and the key is `field:code` because the same
 * code means different things on different fields.
 *
 * Unknown pairs are NAMED rather than dropped: a problem this build has never
 * heard of is still a reason a message will not go out, and swallowing it would
 * leave an author looking at a preview that says everything is fine.
 */
const PROBLEM_WORDS = {
	'title:empty': () => 'Neither the title nor the message renders to any text, so there is nothing to send.',
	'title:required': () => 'This channel needs a title — there is no subject line to fall back on.',
	'title:too-long': p => `The title renders to ${p.actual} characters and this channel carries ${p.limit}. It will be cut.`,
	'body:too-long': p => `The message renders to ${p.actual} characters and this channel carries ${p.limit}. It will be cut.`,
	'text:too-long': p => `Title, message and link come to ${p.actual} characters together; this channel carries ${p.limit}.`,
	'ctas:too-many': p => `${p.actual} buttons, and this channel carries ${p.limit}.`,
	'responses:too-many': p => `${p.actual} reply keywords, and this channel carries ${p.limit}.`,
	'responses:unsupported': () => 'This channel can carry an acknowledgement and nothing richer — a snooze or a create-item button will not appear on it.',
};

const describeProblem = problem => {
	const words = PROBLEM_WORDS[`${problem?.field}:${problem?.code}`];
	return words ? words(problem) : `${problem?.field || 'something'}: ${problem?.code || 'refused'}`;
};

/** The registry's label for a channel key, so `sms` reads as "Text". */
const CHANNEL_LABELS = new Map(resolveChannels(null).map(channel => [channel.key, channel.label]));
const channelLabel = key => CHANNEL_LABELS.get(key) || key;

/** One person's display name, falling back to the id — never a blank row. */
const nameOf = (user, id) => {
	const full = user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
	return full || user?.email || id;
};

/**
 * The sticky side column: what this notification actually says, and who it
 * actually reaches.
 *
 * ⚠ **THE API IS THE AUTHORITY ON THIS SHAPE** — `keepr-api
 * utils/notifications/defs.js previewNotificationDef`, which answers:
 *
 *     { title, body, trigger: {summary, …},
 *       audience: {summary, count, userIds?},
 *       channels: { <key>: {payload, problems?} },
 *       sample: {item_id} | null,
 *       watch?: {scanned?, wouldFire, wouldPass?, items[], truncated?, limit?, skipped?},
 *       sent: false, warnings? }
 *
 * An earlier draft of this rail read `preview.recipients`, `preview.dropped`
 * and `preview.email.{subject,html}` — three fields the endpoint has never
 * sent. It degraded SILENTLY: the Reaches and Left out blocks simply came out
 * empty, which reads as "this reaches nobody", which is worse than an error on
 * the one surface whose whole job is to be believed about who gets told.
 *
 * Every part of it is the SERVER's answer, rendered against real records — the
 * SAVED notification, read by id, so this says what the row as it stands would
 * do rather than what the unsaved draft beside it might. A 400 is shown
 * verbatim.
 *
 * **The recipient list is already the resolved one.** `audience.userIds` comes
 * out of the same readership resolution a real fire runs, so a group of six
 * showing three names IS the finding — the other three cannot see these
 * records. There is deliberately no "left out" list: the endpoint does not
 * report who was intersected away, and a client that guessed would be inventing
 * names. A per-item audience ("whoever is in Assignee on the record") has no
 * count at all before the fire, and says so rather than showing zero.
 *
 * Two things are faithful rather than pretty, the `NotifyPreviewButton` bargain
 * kept:
 *
 * - Buttons render DISABLED. This is a picture of the notification, not the
 *   notification; a live button here would close an alert nobody asked to close.
 * - The email is a `sandbox=""` iframe. It is server-generated HTML, but it is
 *   still a document being injected into the app's own origin, and an empty
 *   sandbox denies everything — scripts, forms, same-origin, navigation.
 */
export default function NotificationPreviewRail({
	notificationId = null,
	buildRequest,
	refreshToken = 0,
	// Why there is nothing to preview yet, when there is not. Never a bare
	// disabled button: the reason sits beside it.
	unavailableReason = null,
}) {
	const { message } = App.useApp();
	const [loading, setLoading] = useState(false);
	const [preview, setPreview] = useState(null);
	const [error, setError] = useState(null);
	const [sending, setSending] = useState(false);
	const [usersById, setUsersById] = useState({});

	// The request thunk may be rebuilt on every keystroke; the FETCH must not.
	// Held in a ref so `run` stays stable — otherwise the effect below would
	// re-fire a request per character, which is the opposite of what it is for.
	const requestRef = useRef(buildRequest);
	requestRef.current = buildRequest;
	const liveRef = useRef(true);
	useEffect(() => () => { liveRef.current = false; }, []);

	const run = useCallback(async () => {
		if (!notificationId) return;
		setLoading(true);
		setError(null);
		try {
			// Read at CALL time, never memoised: the point of a preview is the
			// notification as it stands right now.
			const data = await previewNotificationDef(notificationId, requestRef.current());
			if (liveRef.current) setPreview(data);
		} catch (err) {
			if (!liveRef.current) return;
			setPreview(null);
			setError(apiErrorMessage(err, 'Could not build a preview.'));
		} finally {
			if (liveRef.current) setLoading(false);
		}
	}, [notificationId]);

	// The rail fills itself when the page opens on a saved notification, and
	// again whenever the bar's Preview is pressed. It does NOT follow typing:
	// a request per keystroke would render against half-written templates and
	// cost a query each time.
	useEffect(() => { run(); }, [run, refreshToken]);

	const userIds = useMemo(
		() => (Array.isArray(preview?.audience?.userIds) ? preview.audience.userIds : [])
			.map(normalizeId).filter(Boolean),
		[preview]
	);

	// One batched resolution for every name in the audience — the platform's
	// lookup idiom, the same one NotificationList uses for its faces.
	useEffect(() => {
		if (!userIds.length) { setUsersById({}); return undefined; }
		let active = true;
		getUsersByIds(userIds)
			.then(users => {
				if (!active) return;
				setUsersById(Object.fromEntries((users || []).map(user => [normalizeId(user._id), user])));
			})
			// A name we could not resolve falls back to its id below; the count
			// and the summary beside it are still the answer.
			.catch(() => {});
		return () => { active = false; };
	}, [userIds]);

	const sendTest = async () => {
		setSending(true);
		try {
			await sendNotificationDefTest(notificationId, requestRef.current());
			message.success('Sent to you, and to nobody else.');
		} catch (err) {
			message.error(apiErrorMessage(err, 'That test could not be sent.'));
		} finally {
			setSending(false);
		}
	};

	const warnings = Array.isArray(preview?.warnings) ? preview.warnings : [];
	const channels = preview?.channels || {};
	// In-app renders from its own payload where the definition names the
	// channel, and from the top-level render where it does not — the inbox row
	// is written whichever channels are on (Decision 11), so this block is never
	// empty on account of a switch.
	const inApp = channels.inApp?.payload || null;
	const email = channels.email?.payload || null;
	const title = inApp?.title ?? preview?.title ?? '';
	const body = inApp?.body ?? preview?.body ?? null;
	const ctas = Array.isArray(inApp?.ctas) ? inApp.ctas : [];

	// Every channel that reported a problem, in the registry's order, each one
	// named: an author has to know WHICH channel will cut their title.
	const problems = Object.entries(channels)
		.map(([key, entry]) => ({ key, list: Array.isArray(entry?.problems) ? entry.problems : [] }))
		.filter(entry => entry.list.length);

	// The other channels' rendered payloads, said in one line each. Email has
	// its own block below and in-app is the block above.
	const otherChannels = Object.entries(channels)
		.filter(([key, entry]) => !['email', 'inApp'].includes(key) && entry?.payload);

	const audience = preview?.audience || null;
	// ⚠ `null` AND `0` ARE DIFFERENT ANSWERS and must not be collapsed: null is
	// "resolved per record when it fires" (`describeAudience` sends it for the
	// element audiences), 0 is "resolved, and it is nobody". `Number(null)` is
	// `0`, so the absent case is settled before any arithmetic happens.
	const rawCount = audience?.count;
	const count = (rawCount === null || rawCount === undefined || !Number.isFinite(Number(rawCount)))
		? null
		: Number(rawCount);
	const shown = userIds.slice(0, MAX_NAMES);
	const overflow = userIds.length - shown.length;

	const watch = preview?.watch || null;
	const watchItems = Array.isArray(watch?.items) ? watch.items : [];

	return (
		<section className="form-panel notification-preview" aria-label="Preview">
			<div className="form-panel__kicker-row">
				<div className="micro-label form-panel__kicker">Preview</div>
				{notificationId ? (
					<Button size="small" onClick={run} loading={loading}>Refresh</Button>
				) : null}
			</div>

			{unavailableReason ? <div className="form-panel__hint">{unavailableReason}</div> : null}

			{loading ? <Skeleton active title={false} paragraph={{ rows: 4 }} /> : null}

			{!loading && error ? (
				<Alert type="error" showIcon message="This cannot be sent yet" description={error} />
			) : null}

			{!loading && !error && preview ? (<>
				{warnings.map(warning => <Alert key={warning} type="warning" showIcon message={warning} />)}

				<div className="notification-preview__block">
					<div className="micro-label">In the app</div>
					<div className="notification-preview__card">
						<Text strong className="d-block">{title}</Text>
						{body ? <Paragraph className="notification-preview__body">{body}</Paragraph> : null}
						<div className="notification-preview__ctas">
							{ctas.map((cta, index) => (
								// Disabled on purpose: a preview must not be able to act.
								<Button key={`${cta?.label}-${index}`} size="small" disabled>{cta?.label}</Button>
							))}
						</div>
					</div>
				</div>

				<div className="notification-preview__block">
					<div className="micro-label">Reaches</div>
					{audience?.summary ? (
						<Text className="d-block notification-preview__audience">
							<TermText text={audience.summary} termClassName="notification-preview__term" />
						</Text>
					) : null}

					{count === null ? (
						// A per-item audience is resolved when it fires, against the
						// record that fired it. A count here would be a guess, and a
						// guess on the line that says who gets told is worse than a
						// blank.
						<Text type="secondary" className="d-block">
							Who that is depends on the record, so it is counted when it fires — not now.
						</Text>
					) : count === 0 ? (
						<Text type="secondary" className="d-block">
							Nobody. Everyone in this audience has been narrowed away by what they can actually see.
						</Text>
					) : (<>
						{shown.length ? (
							<ul className="notification-preview__people">
								{shown.map(id => (
									<li key={id}>
										<span className="notification-preview__face" aria-hidden="true">
											<UserAvatar user={usersById[id]} size={20} />
										</span>
										{nameOf(usersById[id], id)}
									</li>
								))}
							</ul>
						) : null}
						<Text type="secondary" className="d-block">
							{joinMeta(
								formatCount(count, 'person', 'people'),
								overflow > 0 ? `${overflow} more not listed` : null
							)}
						</Text>
						{/* The finding, said once: this list is not the audience you
						    picked, it is the audience that survived access. */}
						<Text type="secondary" className="d-block notification-preview__narrowed">
							Already narrowed to the people who can see these records.
						</Text>
					</>)}
				</div>

				{email ? (
					<div className="notification-preview__block">
						<div className="micro-label">In the mail</div>
						<Text type="secondary" className="d-block">{`Subject: ${email.subject || ''}`}</Text>
						<iframe
							title="Email preview"
							className="notification-preview__frame"
							sandbox=""
							srcDoc={email.html || ''}
						/>
					</div>
				) : null}

				{otherChannels.length ? (
					<div className="notification-preview__block">
						<div className="micro-label">On the other channels</div>
						{otherChannels.map(([key, entry]) => (
							<div key={key} className="notification-preview__channel">
								<Text strong className="d-block">{channelLabel(key)}</Text>
								{/* Each channel's payload is its own shape; what a reader
								    wants from it is the words. SMS is one `text` blob,
								    push is a title and a body. */}
								<Paragraph className="notification-preview__body">
									{entry.payload.text || joinMeta(entry.payload.title, entry.payload.body) || '—'}
								</Paragraph>
							</div>
						))}
					</div>
				) : null}

				{problems.length ? (
					<div className="notification-preview__block">
						<div className="micro-label">What a channel will change</div>
						{problems.map(entry => (
							<Alert
								key={entry.key}
								type="warning"
								showIcon
								message={channelLabel(entry.key)}
								description={(
									<ul className="notification-preview__problems">
										{entry.list.map((problem, index) => (
											<li key={index}>{describeProblem(problem)}</li>
										))}
									</ul>
								)}
							/>
						))}
					</div>
				) : null}

				{watch ? (
					<div className="notification-preview__block">
						<div className="micro-label">Inside the ladder today</div>
						{/* A rendered body over one record says nothing about whether
						    anything is actually due, which is the only question worth
						    asking of a date threshold before it runs. */}
						<Text className="d-block">
							{watch.wouldFire
								? `${formatCount(watch.wouldFire, 'notice')} would go out.`
								: 'Nothing is due — no record would be notified by this run.'}
						</Text>
						{/* ⚠ `scanned` AND `wouldPass` ARE OPTIONAL, and their absence
						    is not zero. They are collection-wide totals, so a PERSONAL
						    threshold preview omits them rather than reporting a number
						    about records their owner cannot list — a narrowed-looking
						    total would be a count oracle. Printed through the `|| 'No
						    records'` fallback an absent one read "No records examined",
						    which is the one thing it definitely does not mean. */}
						{typeof watch.scanned === 'number' && Number.isFinite(watch.scanned) ? (
							<Text type="secondary" className="d-block">
								{`${formatCount(watch.scanned, 'record') || 'No records'} examined.`}
							</Text>
						) : null}
						{watch.wouldPass ? (
							// The "why didn't I hear at 60?" answer, before anybody has
							// to ask it: an earlier rung that has already been crossed
							// is recorded as passed rather than fired.
							<Text type="secondary" className="d-block">
								{`${formatCount(watch.wouldPass, 'earlier rung')} already passed — recorded, not sent.`}
							</Text>
						) : null}

						{watchItems.length ? (
							<ul className="notification-preview__watch">
								{watchItems.slice(0, MAX_WATCH_ITEMS).map((entry, index) => (
									<li key={`${entry.item_id || index}`}>
										<span className="notification-preview__watch-title">{entry.title || 'Untitled record'}</span>
										<span className="notification-preview__watch-meta">
											{joinMeta(
												Number.isFinite(Number(entry.offsetDays)) ? `at the ${entry.offsetDays}-day rung` : null,
												// Which entry of the group (KPR-836), as History counts it.
												entry.entry || null,
												entry.label || entry.element || null
											)}
										</span>
										{/* The server's own phrasing of the true distance —
										    which is not the rung: the 30-day rung speaks
										    for a date 27 days out, and pretending otherwise
										    is the bug the rung rule exists to make visible. */}
										{entry.list ? (
											<span className="notification-preview__watch-dates">{entry.list}</span>
										) : null}
									</li>
								))}
							</ul>
						) : null}

						{watchItems.length > MAX_WATCH_ITEMS ? (
							<Text type="secondary" className="d-block">
								{`${watchItems.length - MAX_WATCH_ITEMS} more not listed here.`}
							</Text>
						) : null}

						{watch.truncated ? (
							// No silent caps: a partial scan says so rather than posing
							// as the whole answer.
							<Alert
								type="info"
								showIcon
								message="This scan stopped early"
								description={`The preview looks at the first ${watch.limit || watch.truncated} records, so the figures above are a floor rather than a total. The real run scans them all.`}
							/>
						) : null}

						{watch.skipped ? (
							<Text type="secondary" className="d-block">{`Nothing was scanned: ${watch.skipped}.`}</Text>
						) : null}
					</div>
				) : null}

				<Button className="notification-preview__test" onClick={sendTest} loading={sending} block>
					Send me a test
				</Button>
			</>) : null}
		</section>
	);
}
